import styles from './Editor.module.css';
import React, { ForwardedRef, useEffect } from 'react';
import { Color } from '@tiptap/extension-color';
import { HardBreak } from '@tiptap/extension-hard-break';
import Highlight from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { Youtube } from '@tiptap/extension-youtube';
import { EditorContent, useEditor, type UseEditorOptions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cn from 'classnames';
import { Loader } from '../../widgets/Loader/Loader';
import { DEFAULT_EDITOR_CONFIGURATION, EditorConfiguration } from './EditorConfiguration';
import { EditorMenu } from './EditorMenu';

const getExtensions = (configuration: EditorConfiguration) => {
    const extensions: UseEditorOptions['extensions'] = [
        StarterKit.configure({
            blockquote: configuration.blockquote ? undefined : false,
            bold: configuration.textStyle.bold ? undefined : false,
            bulletList: configuration.lists
                ? {
                      keepMarks: true
                      // keepAttributes: false
                  }
                : false,
            code: configuration.code ? undefined : false,
            codeBlock: configuration.code ? undefined : false,
            heading: configuration.headings ? undefined : false,
            horizontalRule: configuration.horizontalRule ? undefined : false,
            italic: configuration.textStyle.italic ? undefined : false,
            orderedList: configuration.lists
                ? {
                      keepMarks: true
                      // keepAttributes: false
                  }
                : false,
            strike: configuration.textStyle.strike ? undefined : false
        })
    ];

    if (configuration.textColors) {
        extensions.push(
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            Highlight.configure({
                multicolor: true
            })
        );
    }

    if (configuration.textStyle) {
        extensions.push(TextStyle);

        if (configuration.textStyle.underline) {
            extensions.push(Underline);
        }
    }

    if (configuration.table) {
        extensions.push(
            Table.configure({
                resizable: true
            }),
            TableRow,
            TableHeader,
            TableCell
        );
    }

    if (configuration.textAlignment) {
        const types = ['paragraph'];
        if (configuration.headings) {
            types.push('heading');
        }

        extensions.push(
            TextAlign.configure({
                types,
                defaultAlignment: configuration.textAlignment.defaultAlignment
            })
        );
    }

    if (configuration.link) {
        extensions.push(
            Link.configure({
                openOnClick: false
            })
        );
    }

    if (configuration.lists) {
        extensions.push(
            TaskList,
            TaskItem.configure({
                nested: true
            })
        );
    }

    if (configuration.media) {
        extensions.push(Image, Youtube);
    }

    if (configuration.paragraphs === false) {
        extensions.push(
            HardBreak.extend({
                addKeyboardShortcuts() {
                    return {
                        Enter: () => {
                            if (
                                this.editor.isActive('orderedList') ||
                                this.editor.isActive('bulletList')
                            ) {
                                return this.editor.chain().createParagraphNear().run();
                            }
                            return this.editor.commands.setHardBreak();
                        }
                    };
                }
            })
        );
    }

    return extensions;
};

export type EditorProps = {
    content?: string;
    readOnly?: boolean;
    autofocus?: 'start' | 'end' | 'all' | number | boolean | null;
    onChange?: (content: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    className?: string;
    configuration?: EditorConfiguration;
};

export const Editor = React.forwardRef(
    (
        {
            content,
            readOnly,
            autofocus = 'start',
            onChange,
            onFocus,
            onBlur,
            className,
            configuration
        }: EditorProps,
        ref: ForwardedRef<HTMLDivElement | null>
    ) => {
        const _configuration = { ...DEFAULT_EDITOR_CONFIGURATION, ...configuration };
        const editor = useEditor({
            extensions: getExtensions(_configuration),
            content,
            onFocus: () => onFocus?.(),
            onBlur: () => onBlur?.(),
            onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
            editable: !readOnly,
            autofocus
        });

        useEffect(() => {
            if (!editor) return;

            editor.commands.setContent(content || '', false);
        }, [content]);

        useEffect(() => {
            if (!editor) return;

            if (!readOnly) {
                editor.commands.focus(autofocus);
            }

            editor.setOptions({
                editable: !readOnly
            });
        }, [readOnly]);

        if (!editor) return <Loader />;

        return (
            <div className={cn(styles.tiptapContainer, className)}>
                {!readOnly && (
                    <EditorMenu editor={editor} {..._configuration} className={'editor-menu'} />
                )}
                <EditorContent
                    ref={ref}
                    editor={editor}
                    className={cn(
                        styles.tiptapEditor,
                        'editor',
                        _configuration.paragraphs === false && styles.noParagraphs
                    )}
                />
            </div>
        );
    }
);
